/**
 * @prettier
 * @flow
 */
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { paymentLinkApi } from 'src/api/paymentLinkApi';
import { PaymentLinkStatuses } from 'src/constants/PaymentLinkStatus';
import { actions } from 'src/reducers';
import type { PaymentLinkVm } from 'src/types/PaymentLinkVm';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { useAction } from 'src/utils/react/useAction';
import { requireValue } from 'src/utils/require/requireValue';
import { translate } from 'src/utils/translate/translate';

export function CancelPaymentLinkDialog({ open, paymentLink, onClose, onExit, onSuccess }: Props): React.Node {
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(false);
    const showAppSnackBarMessage = useAction(actions.showAppSnackBarMessage);

    const handleClose = () => {
        if (isLoading) return;
        onClose();
    };

    const handleExit = () => {
        if (isLoading) return;
        onExit();
    };

    const handlePressYes = async () => {
        setIsLoading(true);
        if (!paymentLink) return;
        if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.ACTIVE) {
            const response = await paymentLinkApi.cancelPaymentLink({ paymentLinkId: requireValue(paymentLink).paymentLinkId });
            if (!response.ok) {
                alertKnownErrorOrSomethingWentWrong();
                setIsLoading(false);
                return;
            }
            setIsLoading(false);
            onSuccess();
            showAppSnackBarMessage({ message: translate('Cancelled Successfully!') });
        } else if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.PAID) {
            const response = await paymentLinkApi.cancelPaymentLinkWithPayment({ paymentLinkId: requireValue(paymentLink).paymentLinkId });
            if (!response.ok) {
                alertKnownErrorOrSomethingWentWrong();
                setIsLoading(false);
                return;
            }
            setIsLoading(false);
            onSuccess();
            switch (getPaymentLinkCancelType(paymentLink)) {
                case 'CANCEL_PAYMENT':
                    showAppSnackBarMessage({ message: translate('Cancelled Successfully!') });
                    break;
                case 'REFUND_PAYMENT':
                    showAppSnackBarMessage({ message: translate('Refunded Successfully!') });
                    break;
            }
        }
    };

    return (
        <Dialog scroll='paper' open={open} onClose={handleClose} TransitionProps={{ onExit: handleExit }}>
            <DialogTitle>{getTitle(paymentLink)}</DialogTitle>
            <DialogContent></DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    {translate('No').toUpperCase()}
                </Button>
                <Button color='secondary' onClick={handlePressYes} disabled={isLoading}>
                    {translate('Yes').toUpperCase()}
                </Button>
            </DialogActions>
            {isLoading && <LinearProgress className={classes.linearProgress} />}
        </Dialog>
    );
}

function getTitle(paymentLink?: PaymentLinkVm): string {
    let twentyFourHoursAgo = moment().subtract(24, 'hours');
    if (paymentLink?.paidAt && moment(paymentLink.paidAt).isBefore(twentyFourHoursAgo)) {
        return translate('Are you sure you want to Refund this payment?');
    }
    if (paymentLink?.paidAt && moment(paymentLink.paidAt).isAfter(twentyFourHoursAgo)) {
        return translate('Are you sure you want to Cancel this payment?');
    }
    return translate('Are you sure you want to Cancel this payment link?');
}

function getPaymentLinkCancelType(paymentLink?: PaymentLinkVm): 'CANCEL_PAYMENT_LINK' | 'CANCEL_PAYMENT' | 'REFUND_PAYMENT' | 'NA' {
    let twentyFourHoursAgo = moment().subtract(24, 'hours');
    if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.ACTIVE) {
        return 'CANCEL_PAYMENT_LINK';
    }
    if (paymentLink?.paymentLinkStatus === PaymentLinkStatuses.PAID) {
        if (paymentLink?.paidAt && moment(paymentLink.paidAt).isBefore(twentyFourHoursAgo)) {
            return 'REFUND_PAYMENT';
        }
        if (paymentLink?.paidAt && moment(paymentLink.paidAt).isAfter(twentyFourHoursAgo)) {
            return 'CANCEL_PAYMENT';
        }
    }
    return 'NA';
}

const useStyles = makeStyles(theme => ({
    linearProgress: {
        bottom: 0,
        position: 'absolute',
        width: '100%',
    },
}));

type Props = {
    open: boolean,
    paymentLink?: PaymentLinkVm,
    onClose: any => any,
    onExit: any => any,
    onSuccess: any => any,
};
